import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { FiDownload } from "react-icons/fi";
import MainCard from "../../../component/MainCard/MainCard";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import * as actions from "../../../Redux/Actions";
import { showErrorToastMessage } from "../../../component/Toasts/Toasts";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import AssessCandidate from "./AssessCandidate";
import DevelopmentPlanModal from "./DevelopmentPlanModal";
import { downloadSuccessorReportPdf } from "../report/successorReportPdf";
import "../readable-form.scss";

const empName = (e: any) =>
  [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim() || "-";

const buHeadLabel = (e: any) => {
  const parts = [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim();
  return e?.employeeId ? `${parts}-${e.employeeId}` : parts;
};

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-";

/**
 * Assess the successors mapped to a critical role, and build their development plans.
 *
 * Pick the seat first, then work its candidates — mirrors how the data is shaped
 * (candidates hang off a critical role) and keeps each list short.
 * Mapping people onto a seat happens in Critical Roles, not here.
 */
const DevelopmentPlan = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [selectedBuHead, setSelectedBuHead] = useState<any>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showAssess, setShowAssess] = useState(false);
  const [assessData, setAssessData] = useState<any>(null);
  const [showDevPlan, setShowDevPlan] = useState(false);
  const [devCandidate, setDevCandidate] = useState<any>(null);
  // candidate id whose report is currently being built
  const [reportId, setReportId] = useState<string | null>(null);

  const downloadReport = (candidateId: string) => {
    if (!candidateId || reportId) return;
    setReportId(candidateId);
    dispatch(
      actions.getSuccessorReportAction(candidateId, (res: any) => {
        setReportId(null);
        if (res?.error || !res?.data) {
          showErrorToastMessage("Couldn't build the report. Please retry.");
          return;
        }
        try {
          downloadSuccessorReportPdf(res.data);
        } catch (e) {
          showErrorToastMessage("Couldn't generate the PDF.");
        }
      })
    );
  };

  // Everything loads up front and filtering happens client-side: the lists are small
  // (only mapped candidates), so a filter change shouldn't cost a round-trip.
  const loadAll = () => {
    setLoading(true);
    dispatch(
      actions.getCandidatesByCriticalRoleAction(undefined, (res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load successors. Please retry.");
        // Scoping is enforced server-side (a manager only receives their own team).
        else setCandidates(res?.data || []);
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    dispatch(
      actions.getAllCriticalRolesAction((res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load critical roles.");
        else if (res?.data) {
          setRoleOptions([
            { value: "", label: "All roles" },
            ...res.data
              .filter((r: any) => r.designation?.title)
              .map((r: any) => ({
                value: r._id,
                label: r.client?.clientName
                  ? `${r.designation.title} — ${r.client.clientName}`
                  : r.designation.title,
                role: r,
              })),
          ]);
        }
      })
    );
    loadAll();
  }, []);

  const refresh = () => loadAll();

  const roleFilter = selectedRole?.value || "";
  const buHeadFilter = selectedBuHead?.value || "";
  const term = search.trim().toLowerCase();

  // BU Head filter is driven by the roles' buHeadRef: build the dropdown options
  // (distinct BU heads across roles) and a roleRef -> buHeadRef map so we can scope
  // candidates by their seat's BU head.
  const roles = roleOptions.map((o) => o.role).filter(Boolean);
  const roleToBuHead: Record<string, string> = {};
  roles.forEach((r: any) => {
    if (r.buHeadRef) roleToBuHead[r._id] = r.buHeadRef;
  });
  const buHeadOptions = [
    { value: "", label: "All BU heads" },
    ...Array.from(
      roles.reduce((m: Map<string, string>, r: any) => {
        if (r.buHeadRef && !m.has(r.buHeadRef)) m.set(r.buHeadRef, buHeadLabel(r.buHead) || r.buHeadRef);
        return m;
      }, new Map<string, string>())
    ).map(([value, label]) => ({ value, label })),
  ];

  // Match across everything on the row, so searching "HDFC", "Java Developer" or a
  // score works the same as searching a name.
  const matches = (...fields: any[]) =>
    !term ||
    fields.some((f) => f !== null && f !== undefined && String(f).toLowerCase().includes(term));

  const visibleCandidates = candidates.filter(
    (c) =>
      (!roleFilter || c.criticalRoleRef === roleFilter) &&
      (!buHeadFilter || roleToBuHead[c.criticalRoleRef] === buHeadFilter) &&
      matches(
        empName(c.employee),
        c.employee?.employeeId,
        c.employee?.designation,
        c.employee?.clientName,
        c.weightedScoreSnapshot,
        c.readiness,
        c.performance,
        c.potential,
        c.riskOfLoss,
        c.criticalRole?.designation?.title,
        c.criticalRole?.client?.clientName
      )
  );

  const roleLabel = (ref: string) => {
    const o = roleOptions.find((r) => r.value === ref);
    return o?.label || "-";
  };

  // The Designation column shows which designation's KRAs produced this score, so the
  // number needs no extra marker.
  const scoreCell = (c: any) => {
    if (c.weightedScoreSnapshot === null || c.weightedScoreSnapshot === undefined) {
      return <span className="muted">Not scored yet</span>;
    }
    return c.weightedScoreSnapshot;
  };

  return (
    <MainCard>
      <div className="assessment-heading-button-wrapper">
        <div>
          <h2>Development Plan</h2>
        </div>
      </div>

      <div className="dp-filters readable-form mt-3">
        <div className="dp-filter">
          <label className="form-label">Critical Role</label>
          <Select
            className="dropdown-select"
            isSearchable={true}
            placeholder="All roles"
            value={selectedRole}
            isClearable={true}
            options={roleOptions}
            onChange={(sel: any) => setSelectedRole(sel)}
            noOptionsMessage={() => "No critical roles yet — identify one first"}
          />
        </div>
        <div className="dp-filter">
          <label className="form-label">BU Head</label>
          <Select
            className="dropdown-select"
            isSearchable={true}
            placeholder="All BU heads"
            value={selectedBuHead}
            isClearable={true}
            options={buHeadOptions}
            onChange={(sel: any) => setSelectedBuHead(sel)}
          />
        </div>
        <div className="dp-filter">
          <label className="form-label">Search</label>
          <input
            className="form-control"
            placeholder="Employee name or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {(
        <div className="readable-table mt-4">
          <h5>
            Successors<span className="count-chip">{visibleCandidates.length}</span>
          </h5>

          {loading ? (
            <p className="mt-2">Loading…</p>
          ) : visibleCandidates.length > 0 ? (
            <div className="table table-responsive">
              <table className="table-main table table-sm mt-2">
                <thead className="table-head">
                  <tr className="table-head-row">
                    <th className="table-head-row">Employee</th>
                    <th className="table-head-row">Employee ID</th>
                    <th className="table-head-row">Designation</th>
                    <th className="table-head-row">Client</th>
                    {!roleFilter && <th className="table-head-row">Critical Role</th>}
                    <th className="table-head-row">KRA Score</th>
                    <th className="table-head-row">Readiness</th>
                    <th className="table-head-row">Performance</th>
                    <th className="table-head-row">Potential</th>
                    <th className="table-head-row">Risk of Loss</th>
                    <th className="table-head-row">Status</th>
                    <th className="table-head-row">Action</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {visibleCandidates.map((c) => (
                    <tr className="table-body-row" key={c._id}>
                      <td>
                        <span
                          className="seat-link"
                          onClick={() => navigate(`/succession-planning/development-plan/${c._id}`)}
                        >
                          {empName(c.employee)}
                        </span>
                      </td>
                      <td>{c.employee?.employeeId || "-"}</td>
                      <td>{c.employee?.designation || "-"}</td>
                      <td>{c.employee?.clientName || "-"}</td>
                      {!roleFilter && <td>{roleLabel(c.criticalRoleRef)}</td>}
                      <td>{scoreCell(c)}</td>
                      <td>{c.readiness || <span className="muted">-</span>}</td>
                      <td>{c.performance || <span className="muted">-</span>}</td>
                      <td>{c.potential || <span className="muted">-</span>}</td>
                      <td>{c.riskOfLoss || <span className="muted">-</span>}</td>
                      <td>
                        {c.reassessmentDue ? (
                          <span
                            className="due-chip"
                            title={`A development action was due ${fmtDate(c.reassessmentDueSince)} and they have not been reassessed since. Click to reassess.`}
                            onClick={() => {
                              setAssessData(c);
                              setShowAssess(true);
                            }}
                          >
                            Reassess now
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="action-btn seat-actions">
                          <button
                            className="btn add-submit-button buttonsize compact-btn"
                            onClick={() => {
                              setAssessData(c);
                              setShowAssess(true);
                            }}
                          >
                            Assess
                          </button>
                          <button
                            className="btn add-submit-button buttonsize compact-btn"
                            onClick={() => {
                              setDevCandidate(c);
                              setShowDevPlan(true);
                            }}
                          >
                            Dev Plan
                          </button>
                          <button
                            className="cd-eye-btn"
                            title="Download report"
                            disabled={!!reportId}
                            onClick={() => downloadReport(c._id)}
                            style={{ cursor: reportId ? "default" : "pointer" }}
                          >
                            <FiDownload size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-note">
              {candidates.length === 0
                ? "No successors mapped yet — map them in Critical Roles."
                : "No successors match this filter."}
            </p>
          )}
        </div>
      )}

      <ModalComponents
        show={showAssess}
        onHide={() => setShowAssess(false)}
        title="Assess Candidate"
        modalBody={
          // Guarded: the modal stays mounted through its fade-out, and saving nulls
          // assessData — an unguarded render then dereferences null. Same pattern as
          // DevelopmentPlanModal below.
          assessData ? (
            <AssessCandidate
              candidate={assessData}
              setShowAssess={setShowAssess}
              setAssessData={setAssessData}
              onSaved={refresh}
            />
          ) : null
        }
        className="modal-md"
      />

      <ModalComponents
        show={showDevPlan}
        onHide={() => {
          setShowDevPlan(false);
          // the modal has no save callback, so pick up whatever it added on close
          refresh();
        }}
        title="Development Plan"
        modalBody={devCandidate ? <DevelopmentPlanModal candidate={devCandidate} /> : null}
        className="modal-xl"
      />
    </MainCard>
  );
};

export default DevelopmentPlan;
